import type { NightmareType } from './nightmares.ts';
import { res, type Resources, type StructureType } from './structures.ts';
import { hoursMs, sec } from './units.ts';

/**
 * Eras of the bastion (docs/ERAS.md): the group grows the bastion with walled districts, each raised
 * by a Great Work on the keep. Two districts per era: the first one of an era opens it.
 */
export type DistrictKind = 'military' | 'quarry' | 'sacred' | 'guild';
export const DISTRICT_KINDS: DistrictKind[] = ['military', 'quarry', 'sacred', 'guild'];

export const DISTRICT = {
  /** A district is the ball of this radius around its centre (interior: distance < radius). */
  radius: 4,
  /** Its centre sits this far beyond the ring, on the middle of a side of the bastion. */
  reach: 3,
  /** Sides of the bastion (one district each). */
  sides: 6,
};

/**
 * Era n + 1 (index n) opens with district 2n; its districts need the group at `level`: the average level of the
 * active heroes, rounded down (docs/PROGRESION.md; it was every active hero at 5 and 8 until 2026-10-28).
 */
export const ERAS: { level: number; costPerPlayer: Resources; buildMs: number; crown?: boolean }[] = [
  { level: 5, costPerPlayer: res({ wood: 250, stone: 250, crystal: 100 }), buildMs: hoursMs(24) },
  { level: 12, costPerPlayer: res({ wood: 500, stone: 500, crystal: 200 }), buildMs: hoursMs(48) },
  /**
   * Era IV · the Vigil's Bastion: a crowning, not a district. Its Great Work restores the keep (docs/ERAS.md): some
   * maps hold only four districts, so the late eras can't hang on a free side.
   */
  { level: 20, costPerPlayer: res({ wood: 1000, stone: 1000, crystal: 400 }), buildMs: hoursMs(72), crown: true },
  /** Era V · Somnhold: the second crowning restores the whole fortress as it was before the Night of the Gate. */
  { level: 30, costPerPlayer: res({ wood: 2000, stone: 2000, crystal: 800 }), buildMs: hoursMs(96), crown: true },
];

/** The era a forge level asks (docs/PROGRESION.md): the forge grows with the bastion. */
export const FORGE_LEVEL_ERA: Record<number, number> = { 4: 3, 5: 4, 6: 5 };

/**
 * Era V's Dream Beacon on the crowned keep: once a wave, `at` ticks in, it pulses and the spectres fight at
 * `powerBp` (or their own, if more) for `ticks`, as if their players were there.
 */
export const DREAM_BEACON = { at: sec(40), ticks: sec(12), powerBp: 10000 };

/** Era V's titan stone: the ring wall and its gates take this share of every blow. */
export const TITAN_STONE = { takenBp: 6500 };

/** The entry of an era-keyed table for this era: its own, or the latest era's before it (a new era keeps the last one's). */
export const upToEra = <T>(table: Record<number, T>, era: number): T | undefined => {
  for (let e = era; e >= 1; e--) if (table[e] !== undefined) return table[e];
  return undefined;
};
export const DISTRICTS_PER_ERA = 2;
/** Eras II and III each raise two districts; the eras after them are crownings. */
export const MAX_DISTRICTS = ERAS.filter((e) => !e.crown).length * DISTRICTS_PER_ERA;

/** While a Great Work stands unfinished on the keep, nightmares hurt the keep this much more. */
export const GREAT_WORK_KEEP_FRAIL_BP = 13000;
/** Losing a Marea with a Great Work under way costs a quarter of it (paid or done). */
export const GREAT_WORK_LOSS_BP = 2500;

/** What each kind of district gives. */
export const DISTRICT_FX = {
  /** Military: what's built or improved inside costs less. */
  military: { costBp: 7500 },
  /** Quarry: after every wave, wood and stone per active player. */
  quarry: { perPlayer: res({ wood: 12, stone: 12 }) },
  /** Sacred: after every wave, what stands inside mends this share of its HP. */
  sacred: { healBp: 3000 },
  /** Guild: one more golem. */
  guild: { golems: 1 },
};

/**
 * How many of each tower an era allows (built, ruined or planned), for a group of 4. What already stands
 * above it stays; only new plans are refused. Era II doubles it, era III triples it; bigger groups get
 * more (a third more at 6 players, two thirds more at 8), smaller ones less.
 */
export const ERA_TOWER_CAPS: Partial<Record<StructureType, number>> = { ballista: 9, catapult: 6, arcane: 8, belfry: 4, beacon: 4, clockwork: 3, trebuchet: 1 };
export const eraTowerCap = (type: StructureType, era: number, players: number): number | null => {
  const base = ERA_TOWER_CAPS[type];
  if (base === undefined) return null;
  const n = players < 1 ? 1 : players;
  return Math.max(1, Math.trunc((base * era * (2 + n) + 3) / 6));
};

/** The alchemist's laboratory: enchantments last longer, and an elixir can be brewed for the next wave. */
export const LAB = {
  /** How long a mage's enchantment lasts with a laboratory standing (bp of the usual 24 h). */
  enchantBp: 20000,
};
export type ElixirKind = 'might' | 'vigor' | 'ward';
export const ELIXIR_KINDS: ElixirKind[] = ['might', 'vigor', 'ward'];
/**
 * One elixir for the next wave, for every hero who fights it; dust per player in the campaign. Ward: an
 * affinity of `resBp` against the damage type the wave mostly brings (what the omen names), or `allBp`
 * against every type when it brings no one type.
 */
export const ELIXIRS: Record<ElixirKind, { damageBp?: number; hpBp?: number; resBp?: number; allBp?: number; dustPerPlayer: number }> = {
  might: { damageBp: 1500, dustPerPlayer: 60 },
  vigor: { hpBp: 2000, dustPerPlayer: 60 },
  ward: { resBp: 3000, allBp: 1000, dustPerPlayer: 60 },
};

/** The artificer's workshop: repairs go twice as fast; a clockwork turret overloaded by an artificer bursts, then rests. */
export const WORKSHOP = {
  repairTimeBp: 5000,
  /** Ticks an overloaded clockwork turret stays silent. */
  overloadRest: 120,
};

/**
 * The paladin's temple (era I): towers within `range` hit the inked and the undead `towerBp` harder;
 * with a paladin in the fight, structures within it take `takenBp` of the damage.
 */
export const TEMPLE = { range: 2500, towerBp: 11500, takenBp: 8000 };

/**
 * The druid's grove (era II): wood after every wave (by era; `druidBp` with a druid active in the
 * campaign); in a fight its roots slow nightmares within `range` by `slowBp` (`druidSlowBp` while a
 * druid fights).
 */
export const GROVE = { woodPerWave: [0, 0, 15, 20] as readonly number[], druidBp: 20000, range: 3000, slowBp: 2500, druidSlowBp: 4000 };

/**
 * The barbarian's clan hall (era III): every warlord or boss the group fells this Marea hangs a trophy
 * (up to `maxTrophies`), each `trophyBp` more damage for every hero in its waves; with a barbarian in
 * the fight the war drums make everyone `drumsBp` faster for the first `drumsTicks`.
 */
export const HALL = { maxTrophies: 5, trophyBp: 200, drumsBp: 11500, drumsTicks: 240 };

/**
 * The ranger's watchtower (era IV): towers within `range` (STRUCTURES.watchtower.range) reach `rangeBp` farther
 * (`rangerRangeBp` while a ranger fights). Within `reveal` (`rangerReveal` with a ranger) it shows the invisible
 * and marks what goes under the ground, so it can be seen and hit before it surfaces (see `spotted` in sim).
 */
export const WATCHTOWER = { rangeBp: 1500, rangerRangeBp: 2500, reveal: 4500, rangerReveal: 6000 };

/**
 * The cleric's sanctum (era IV): after every wave what stands within its range (STRUCTURES.sanctum.range) mends
 * `healBp` of its HP (`clericHealBp` with a cleric in the group); in a fight the heroes within it heal `blessBp` of
 * their health every second (`clericBlessBp` while a cleric fights).
 */
export const SANCTUM = { healBp: 1500, clericHealBp: 2500, blessBp: 60, clericBlessBp: 120 };

/** The trebuchet (era IV): what its stones deal to the big ones (giants, colossi, siege engines, warlords, bosses), in bp; it aims at them first. */
export const TREBUCHET = { heavyBp: 15000 };

/** The iron mines (era II): what one gives, as a share of a normal haul (iron is scarce). */
export const IRON_MINE = { rewardBp: 3500 };

/** Nightmares an era adds to every wave's table (weights, on top of the Marea's). */
export const ERA_NIGHTMARES: Record<number, Partial<Record<NightmareType, number>>> = {
  2: { sapper: 1, ram: 1, mangonel: 1, mammoth: 1, archer: 1, troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1 },
  3: { sapper: 2, ram: 2, mangonel: 2, mammoth: 2, archer: 2, troll: 2, beetle: 2, bannerman: 2, bonesinger: 2, wyvern: 2, giant: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1 },
  /** Era IV (docs/CRIATURAS.md): the old garrison dreamt twisted and the weather of the shell. */
  4: { sapper: 2, ram: 2, mangonel: 2, mammoth: 2, archer: 2, troll: 2, beetle: 2, bannerman: 2, bonesinger: 2, wyvern: 2, giant: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1, sentinel: 2, frostColossus: 1, stormHerald: 1, shellSerpent: 1 },
  /** Era V (docs/CRIATURAS.md): the dragon's heralds and things the size of the shell. */
  5: { sapper: 2, ram: 2, mangonel: 2, mammoth: 2, archer: 2, troll: 2, beetle: 2, bannerman: 2, bonesinger: 2, wyvern: 2, giant: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1, sentinel: 2, frostColossus: 1, stormHerald: 1, shellSerpent: 1, glassDrake: 1, shellback: 1, lidlessDreamer: 1, ashWatcher: 2 },
};

/**
 * What an era adds to incursions and crypts (weights): the creatures, not the siege engines (no gates or
 * walls out there to dig under, batter or shoot past).
 */
export const ERA_DELVES: Record<number, Partial<Record<NightmareType, number>>> = {
  2: { troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1 },
  3: { troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1 },
  /** Era IV out there: the sentinels, the colossus and the herald (the serpent hunts towers, there are none). */
  4: { troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1, sentinel: 1, frostColossus: 1, stormHerald: 1 },
  /** Era V out there: the drake, the dreamer and the watchers (the shellback is a siege thing). */
  5: { troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1, sentinel: 1, frostColossus: 1, stormHerald: 1, glassDrake: 1, lidlessDreamer: 1, ashWatcher: 1 },
};

/** The sappers' fort (era II incursion): mostly sappers; win it and the next wave comes without them. */
export const SAPPER_FORT = { weights: { sapper: 14, archer: 2 } as Partial<Record<NightmareType, number>> };
